/* global IntersectionObserver */

import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { Dropdown } from "react-bootstrap";
import SearchInput from "~/components/forms/SearchInput";
import { labelApi } from "~/pages/Labels/api/labelApi";

const initialState = {
  inputValue: "",
  suggestions: [],
  page: 1,
  hasNextPage: false,
  isLoading: false,
  isListVisible: false,
  error: null,
  isItemSelected: false,
};

function autosuggestReducer(state, action) {
  switch (action.type) {
    case "START_FETCH":
      return { ...state, isLoading: true, error: null };
    case "FETCH_SUCCESS":
      return {
        ...state,
        isLoading: false,
        isListVisible: true,
        suggestions: action.payload.labels,
        hasNextPage: !!action.payload.nextPage,
      };
    case "FETCH_MORE_SUCCESS":
      return {
        ...state,
        isLoading: false,
        isListVisible: true,
        suggestions: [...state.suggestions, ...action.payload.labels],
        hasNextPage: !!action.payload.nextPage,
      };
    case "FETCH_FAILURE":
      return { ...state, isLoading: false, error: action.payload.error };
    case "UPDATE_INPUT":
      return {
        ...state,
        inputValue: action.payload.value,
        isItemSelected: false,
      };
    case "SET_PAGE":
      return { ...state, page: action.payload.page };
    case "SELECT_ITEM":
      return {
        ...state,
        isListVisible: false,
        inputValue: action.payload.name,
        isItemSelected: true,
        suggestions: [],
        hasNextPage: false,
      };
    case "CLEAR":
      return { ...initialState };
    case "CLEAR_SUGGESTIONS":
      return {
        ...state,
        suggestions: [],
        isListVisible: false,
        hasNextPage: false,
      };
    case "SET_LIST_VISIBILITY":
      return {
        ...state,
        isListVisible:
          action.payload.show &&
          state.inputValue.length >= 2 &&
          state.suggestions.length > 0,
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

const CustomSearchToggle = forwardRef(
  (
    {
      value,
      onChange,
      onFocus,
      onClear,
      isLoading,
      placeholder,
      disabled,
      hasError,
    },
    ref,
  ) => (
    <SearchInput
      ref={ref}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onClear={onClear}
      isLoading={isLoading}
      placeholder={placeholder}
      disabled={disabled}
      hasError={hasError}
    />
  ),
);

const AutosuggestInput = forwardRef(
  (
    {
      onSelect,
      onInputChange = null,
      value = "",
      placeholder = "Search for labels...",
      disabled = false,
      hasError = false,
    },
    ref,
  ) => {
    const [state, dispatch] = useReducer(autosuggestReducer, {
      ...initialState,
      inputValue: value,
      isItemSelected: !!value,
    });
    const {
      inputValue,
      suggestions,
      page,
      hasNextPage,
      isLoading,
      isListVisible,
      error,
      isItemSelected,
    } = state;

    const observerRef = useRef(null);

    useImperativeHandle(ref, () => ({
      clear() {
        dispatch({ type: "CLEAR" });
      },
    }));

    const fetchSuggestions = useCallback(
      async (query, pageNumber, isLoadMore = false) => {
        if (!query) {
          return;
        }

        dispatch({ type: "START_FETCH" });
        try {
          const response = await labelApi.fetchLabels({
            s: { query },
            page: pageNumber,
          });
          const actionType = isLoadMore
            ? "FETCH_MORE_SUCCESS"
            : "FETCH_SUCCESS";
          dispatch({
            type: actionType,
            payload: {
              labels: response.labels || [],
              nextPage: response.pagination.next,
            },
          });
        } catch (e) {
          dispatch({
            type: "FETCH_FAILURE",
            payload: { error: "Failed to fetch suggestions." },
          });
        }
      },
      [],
    );

    const debouncedFetch = useMemo(
      () =>
        debounce((query) => {
          dispatch({ type: "SET_PAGE", payload: { page: 1 } });
          fetchSuggestions(query, 1);
        }, 350),
      [fetchSuggestions],
    );

    const handleInputChange = (event) => {
      const newValue = event.target.value;
      dispatch({ type: "UPDATE_INPUT", payload: { value: newValue } });

      if (onInputChange) {
        onInputChange(newValue);
      }
    };

    const handleSelect = (label) => {
      dispatch({ type: "SELECT_ITEM", payload: { name: label.name } });
      if (onSelect) {
        onSelect(label);
      }
    };

    const handleLoadMore = useCallback(() => {
      const nextPage = page + 1;
      dispatch({ type: "SET_PAGE", payload: { page: nextPage } });
      fetchSuggestions(inputValue, nextPage, true);
    }, [page, inputValue, fetchSuggestions]);

    useEffect(() => {
      if (!isItemSelected) {
        const trimmedInput = inputValue.trim();

        if (trimmedInput.length >= 2) {
          debouncedFetch(trimmedInput);
        } else {
          debouncedFetch.cancel();
          dispatch({ type: "CLEAR_SUGGESTIONS" });
        }
      }

      return () => debouncedFetch.cancel();
    }, [inputValue, debouncedFetch, isItemSelected]);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (firstEntry.isIntersecting && hasNextPage && !isLoading) {
            handleLoadMore();
          }
        },
        { threshold: 1.0 },
      );

      const currentObserverRef = observerRef.current;

      if (currentObserverRef) {
        observer.observe(currentObserverRef);
      }

      return () => {
        if (currentObserverRef) {
          observer.unobserve(currentObserverRef);
        }
      };
    }, [hasNextPage, isLoading, handleLoadMore]);

    return (
      <Dropdown
        show={!!isListVisible}
        onToggle={(isOpen) =>
          dispatch({ type: "SET_LIST_VISIBILITY", payload: { show: isOpen } })
        }
      >
        <Dropdown.Toggle
          as={CustomSearchToggle}
          id="dropdown-custom-search"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() =>
            dispatch({ type: "SET_LIST_VISIBILITY", payload: { show: true } })
          }
          onClear={() => {
            dispatch({ type: "CLEAR" });
            if (onInputChange) {
              onInputChange("");
            }
          }}
          isLoading={isLoading && page === 1}
          placeholder={placeholder}
          disabled={disabled}
          hasError={hasError}
        />

        <Dropdown.Menu
          className="w-100 overflow-auto"
          style={{ maxHeight: "250px" }}
        >
          {suggestions.map((label) => (
            <Dropdown.Item key={label.id} onClick={() => handleSelect(label)}>
              {label.name}
            </Dropdown.Item>
          ))}

          {!isLoading && isListVisible && suggestions.length === 0 && (
            <Dropdown.ItemText className="text-muted text-center">
              No results found. Type to create a new label.
            </Dropdown.ItemText>
          )}

          {hasNextPage && (
            <div ref={observerRef}>
              <Dropdown.ItemText className="text-muted text-center py-2">
                {isLoading ? "Loading..." : ""}
              </Dropdown.ItemText>
            </div>
          )}
        </Dropdown.Menu>

        {error && <small className="form-text text-danger mt-1">{error}</small>}
      </Dropdown>
    );
  },
);

AutosuggestInput.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
};

export default AutosuggestInput;
